/**
 * SCRUM-411 — Clases grupales: operación de sesiones, inscripciones,
 * check-in y ocupación. Reusa Servicios (tipo de clase) y Profesionales
 * (instructor) ya existentes — acá solo se administra lo genuinamente
 * nuevo: sesiones puntuales + inscriptos con cupo.
 */
import { createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useCallback, useEffect, useState } from "react";
import { ClinicalPageShell, ClinicalStatCard } from "@/components/clinical";
import { CrudShellHeader, Loading, CrudEmptyState } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { CalendarRange, CheckCircle2, Plus, UserCheck, UserX, Users, XCircle } from "lucide-react";
import type { ServiceCatalogItem } from "@/lib/db-service/service-catalog";
import type { ServiceProfessional } from "@/lib/db-service/service-professionals";
import type { ClassSession, ClassEnrollment } from "@/lib/db-service/service-class-sessions";

export const Route = createFileRoute("/app/clases")({
  validateSearch: (s: Record<string, unknown>) => ({
    serviceId: typeof s.serviceId === "string" ? s.serviceId : undefined,
  }),
  component: Page,
  head: () => ({ meta: [{ title: "Clases" }] }),
});

const DAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

function Page() {
  const tenantId = useWorkspaceTenantId();
  const { serviceId: serviceIdFromUrl } = Route.useSearch();
  const [loading, setLoading] = useState(true);
  const [classServices, setClassServices] = useState<ServiceCatalogItem[]>([]);
  const [professionals, setProfessionals] = useState<ServiceProfessional[]>([]);
  const [sessions, setSessions] = useState<ClassSession[]>([]);
  const [serviceFilter, setServiceFilter] = useState(serviceIdFromUrl ?? "");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [metrics, setMetrics] = useState<{
    totalSessions: number;
    totalCapacity: number;
    totalEnrolled: number;
    occupancyPct: number;
  } | null>(null);

  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({
    serviceId: serviceIdFromUrl ?? "",
    professionalId: "",
    dayOfWeek: 1,
    startTime: "18:00",
    endTime: "19:00",
    capacity: "10",
    startDate: new Date().toISOString().slice(0, 10),
    occurrences: "8",
  });
  const [generating, setGenerating] = useState(false);

  const [detailSession, setDetailSession] = useState<ClassSession | null>(null);
  const [detailEnrollments, setDetailEnrollments] = useState<ClassEnrollment[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", phone: "" });
  const [addingEnrollment, setAddingEnrollment] = useState(false);
  const [cancelSessionTarget, setCancelSessionTarget] = useState<ClassSession | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [servicesRes, profsRes, sessionsRes, metricsRes] = await Promise.all([
        apiGetJson<{ items: ServiceCatalogItem[] }>("/api/service-admin?resource=services", tenantId),
        apiGetJson<{ items: ServiceProfessional[] }>("/api/service-admin?resource=professionals", tenantId),
        apiGetJson<{ items: ClassSession[] }>(
          `/api/class-admin?resource=sessions${serviceFilter ? `&serviceId=${serviceFilter}` : ""}${dateFrom ? `&dateFrom=${dateFrom}` : ""}${dateTo ? `&dateTo=${dateTo}` : ""}`,
          tenantId,
        ),
        apiGetJson<{
          metrics: {
            totalSessions: number;
            totalCapacity: number;
            totalEnrolled: number;
            occupancyPct: number;
          };
        }>("/api/class-admin?resource=metrics", tenantId),
      ]);
      setClassServices((servicesRes.items ?? []).filter((s) => s.sessionType === "group"));
      setProfessionals(profsRes.items ?? []);
      setSessions(sessionsRes.items ?? []);
      setMetrics(metricsRes.metrics);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [tenantId, serviceFilter, dateFrom, dateTo]);

  useEffect(() => {
    void load();
  }, [load]);

  async function generateSeries() {
    if (!tenantId) return;
    if (!genForm.serviceId || !genForm.professionalId) {
      toast.error("Elegí la clase y el profesional");
      return;
    }
    setGenerating(true);
    try {
      const r = await apiSendJson<{ items: ClassSession[] }>(
        "/api/class-admin?resource=generate-series",
        "POST",
        {
          serviceId: genForm.serviceId,
          professionalId: genForm.professionalId,
          dayOfWeek: genForm.dayOfWeek,
          startTime: genForm.startTime,
          endTime: genForm.endTime,
          capacity: Number(genForm.capacity || 1),
          startDate: genForm.startDate,
          occurrences: Number(genForm.occurrences || 1),
        },
        tenantId,
      );
      toast.success(`${r.items.length} sesiones creadas`);
      setGenOpen(false);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo generar la serie");
    } finally {
      setGenerating(false);
    }
  }

  async function openDetail(session: ClassSession) {
    if (!tenantId) return;
    setDetailSession(session);
    setDetailLoading(true);
    setAddForm({ name: "", email: "", phone: "" });
    try {
      const r = await apiGetJson<{ session: ClassSession; enrollments: ClassEnrollment[] }>(
        `/api/class-admin?resource=session&id=${session.id}`,
        tenantId,
      );
      setDetailSession(r.session);
      setDetailEnrollments(r.enrollments);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cargar el detalle");
    } finally {
      setDetailLoading(false);
    }
  }

  async function refreshDetail() {
    if (detailSession) await openDetail(detailSession);
  }

  async function addEnrollment() {
    if (!tenantId || !detailSession) return;
    if (!addForm.name.trim() || !addForm.email.trim()) {
      toast.error("Nombre y email son obligatorios");
      return;
    }
    setAddingEnrollment(true);
    try {
      const r = await apiSendJson<{ error?: string }>(
        "/api/class-admin?resource=enroll",
        "POST",
        {
          sessionId: detailSession.id,
          customerName: addForm.name,
          customerEmail: addForm.email,
          customerPhone: addForm.phone || undefined,
        },
        tenantId,
      );
      if (r.error) {
        toast.error(
          r.error === "CLASS_FULL"
            ? "La clase está completa"
            : r.error === "ALREADY_ENROLLED"
              ? "Ya está inscripto/a"
              : "No se pudo inscribir",
        );
        return;
      }
      toast.success("Inscripción agregada");
      setAddForm({ name: "", email: "", phone: "" });
      await refreshDetail();
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo inscribir");
    } finally {
      setAddingEnrollment(false);
    }
  }

  async function cancelEnrollment(id: string) {
    if (!tenantId) return;
    try {
      await apiDelete(`/api/class-admin?resource=enrollment&id=${id}`, tenantId);
      toast.success("Inscripción cancelada");
      await refreshDetail();
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cancelar");
    }
  }

  async function checkIn(enrollmentId: string) {
    if (!tenantId) return;
    try {
      const r = await apiSendJson<{ error?: string }>(
        "/api/class-admin?resource=checkin",
        "POST",
        { enrollmentId },
        tenantId,
      );
      if (r.error) {
        toast.error("No se pudo registrar el check-in");
        return;
      }
      toast.success("Check-in registrado");
      await refreshDetail();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo registrar el check-in");
    }
  }

  async function confirmCancelSession() {
    if (!tenantId || !cancelSessionTarget) return;
    try {
      await apiSendJson(
        `/api/class-admin?resource=sessions&id=${cancelSessionTarget.id}`,
        "PATCH",
        {},
        tenantId,
      );
      toast.success("Sesión cancelada");
      setCancelSessionTarget(null);
      if (detailSession?.id === cancelSessionTarget.id) setDetailSession(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cancelar la sesión");
    }
  }

  const linkedProfessionals = professionals.filter((p) => p.active);

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="clases" />
      <CrudShellHeader
        title="Clases"
        subtitle="Sesiones, inscripciones y check-in"
        count={sessions.length}
        actions={
          <Button
            size="sm"
            className="gap-2"
            onClick={() => setGenOpen(true)}
            disabled={classServices.length === 0}
          >
            <Plus className="h-4 w-4" /> Generar sesiones
          </Button>
        }
      />

      {classServices.length === 0 && !loading ? (
        <div className="px-4 pb-8">
          <CrudEmptyState
            title="Todavía no hay clases grupales"
            description='Marcá un servicio como "Es una clase grupal" desde Servicios para empezar a programar sesiones acá.'
          />
        </div>
      ) : (
        <div className="space-y-4 px-4 pb-8">
          {metrics ? (
            <div className="grid gap-3 sm:grid-cols-4">
              <ClinicalStatCard
                label="Sesiones"
                value={metrics.totalSessions}
                icon={CalendarRange}
                variant="primary"
              />
              <ClinicalStatCard label="Cupo total" value={metrics.totalCapacity} icon={Users} />
              <ClinicalStatCard
                label="Inscriptos"
                value={metrics.totalEnrolled}
                icon={UserCheck}
                variant="success"
              />
              <ClinicalStatCard label="Ocupación" value={`${metrics.occupancyPct}%`} icon={CheckCircle2} />
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Select
              value={serviceFilter || "__all"}
              onValueChange={(v) => setServiceFilter(v === "__all" ? "" : v)}
            >
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Todas las clases" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Todas las clases</SelectItem>
                {classServices.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              className="w-40"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <Input type="date" className="w-40" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>

          {loading ? (
            <Loading />
          ) : sessions.length === 0 ? (
            <CrudEmptyState
              title="Sin sesiones"
              description="Generá sesiones para esta clase con el botón de arriba."
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {sessions.map((s) => {
                const full = s.enrolledCount >= s.capacity;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => void openDetail(s)}
                    className="flex flex-col rounded-xl border bg-card p-4 text-left transition-colors hover:border-primary/30"
                    data-testid="class-session-card"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold">{s.serviceName}</p>
                      {s.status === "cancelled" ? (
                        <Badge variant="outline" className="text-[10px]">
                          Cancelada
                        </Badge>
                      ) : full ? (
                        <Badge variant="secondary" className="text-[10px]">
                          Completa
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground">{s.professionalName}</p>
                    <p className="mt-2 text-sm">
                      {s.sessionDate} · {s.startTime}–{s.endTime}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {s.enrolledCount}/{s.capacity} inscriptos
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Generar serie de sesiones */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generar sesiones</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Clase</Label>
              <Select
                value={genForm.serviceId}
                onValueChange={(v) => setGenForm((f) => ({ ...f, serviceId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Elegir clase…" />
                </SelectTrigger>
                <SelectContent>
                  {classServices.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Instructor</Label>
              <Select
                value={genForm.professionalId}
                onValueChange={(v) => setGenForm((f) => ({ ...f, professionalId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Elegir instructor…" />
                </SelectTrigger>
                <SelectContent>
                  {linkedProfessionals.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Día de la semana</Label>
              <Select
                value={String(genForm.dayOfWeek)}
                onValueChange={(v) => setGenForm((f) => ({ ...f, dayOfWeek: Number(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map((d, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Desde hora</Label>
                <Input
                  type="time"
                  value={genForm.startTime}
                  onChange={(e) => setGenForm((f) => ({ ...f, startTime: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Hasta hora</Label>
                <Input
                  type="time"
                  value={genForm.endTime}
                  onChange={(e) => setGenForm((f) => ({ ...f, endTime: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Cupo</Label>
                <Input
                  type="number"
                  min={1}
                  value={genForm.capacity}
                  onChange={(e) => setGenForm((f) => ({ ...f, capacity: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Desde</Label>
                <Input
                  type="date"
                  value={genForm.startDate}
                  onChange={(e) => setGenForm((f) => ({ ...f, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Repeticiones</Label>
                <Input
                  type="number"
                  min={1}
                  max={52}
                  value={genForm.occurrences}
                  onChange={(e) => setGenForm((f) => ({ ...f, occurrences: e.target.value }))}
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Crea una sesión semanal en ese día/horario, empezando en la fecha elegida. No duplica si ya
              existe una sesión en ese slot exacto.
            </p>
            <Button className="w-full" disabled={generating} onClick={() => void generateSeries()}>
              {generating ? "Generando…" : "Generar sesiones"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detalle de sesión: inscriptos + check-in */}
      <Dialog open={detailSession != null} onOpenChange={(v) => !v && setDetailSession(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          {detailSession ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {detailSession.serviceName} · {detailSession.sessionDate} {detailSession.startTime}–
                  {detailSession.endTime}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>
                    {detailSession.professionalName} · {detailSession.enrolledCount}/{detailSession.capacity}{" "}
                    cupos
                  </span>
                  {detailSession.status === "scheduled" ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => setCancelSessionTarget(detailSession)}
                    >
                      Cancelar sesión
                    </Button>
                  ) : (
                    <Badge variant="outline">Cancelada</Badge>
                  )}
                </div>

                {detailSession.status === "scheduled" &&
                detailSession.enrolledCount < detailSession.capacity ? (
                  <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
                    <p className="text-xs font-medium">Agregar inscripción manual</p>
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder="Nombre"
                        value={addForm.name}
                        onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                      />
                      <Input
                        placeholder="Email"
                        value={addForm.email}
                        onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                      />
                      <Input
                        placeholder="Teléfono"
                        value={addForm.phone}
                        onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
                      />
                    </div>
                    <Button size="sm" disabled={addingEnrollment} onClick={() => void addEnrollment()}>
                      {addingEnrollment ? "Agregando…" : "Agregar"}
                    </Button>
                  </div>
                ) : null}

                {detailLoading ? (
                  <Loading />
                ) : detailEnrollments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Todavía no hay inscriptos.</p>
                ) : (
                  <div className="space-y-2">
                    {detailEnrollments.map((e) => (
                      <div
                        key={e.id}
                        className="flex items-center justify-between gap-2 rounded-lg border p-3 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium">{e.customerName}</p>
                          <p className="truncate text-xs text-muted-foreground">{e.customerEmail}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {e.status === "attended" ? (
                            <Badge variant="secondary" className="gap-1 text-[10px]">
                              <UserCheck className="h-3 w-3" /> Asistió
                            </Badge>
                          ) : e.status === "cancelled" ? (
                            <Badge variant="outline" className="gap-1 text-[10px]">
                              <UserX className="h-3 w-3" /> Cancelada
                            </Badge>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => void checkIn(e.id)}
                                data-testid="manual-checkin-button"
                              >
                                Check-in
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => void cancelEnrollment(e.id)}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={cancelSessionTarget != null}
        onOpenChange={(v) => !v && setCancelSessionTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar esta sesión?</AlertDialogTitle>
            <AlertDialogDescription>
              Se avisa por email a todos los inscriptos y se liberan sus lugares. Esta acción no se puede
              deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void confirmCancelSession()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancelar sesión
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClinicalPageShell>
  );
}
